<!-- SeriesSwitcher.vue -->
<!-- Substitui o <h2 class="main-title"> estático do TextDisplayer: o nome da     -->
<!-- série ativa vira um menu com todas as séries do texto + "Nova série".        -->
<!-- Trocar ou criar acontece in-place (activityStore.switchSeries), sem navegar. -->
<script setup>
import { ref, computed, onMounted, onBeforeUnmount, nextTick } from "vue";
import { useRouter } from "vue-router";
import { toTitleCase } from "@/utils/stringUtils";
import * as activityService from "@/services/activityService";
import { useActivityStore } from "@/stores/useActivityStore";
import { useTextStore } from "@/stores/useTextStore";
import { useUserStore } from "@/stores/useUserStore";

const emit = defineEmits(["open-activity-card"]);

const router = useRouter();
const activityStore = useActivityStore();
const textStore = useTextStore();
const userStore = useUserStore();

const currentUserId = computed(() => userStore.currentUser?.userId ?? null);
// Numa lista de texto compartilhado, "padrão" só faz sentido para as séries do
// próprio usuário — cada pessoa tem a sua série canônica.
function isOwnDefault(s) {
  return s.isDefault && s.createdByUserId === currentUserId.value;
}
function isOwn(s) {
  return s.createdByUserId === currentUserId.value;
}

const open = ref(false);
const loading = ref(false);
const busy = ref(false);
const error = ref("");
const series = ref([]);

const creating = ref(false);
const newTitle = ref("");
const newTitleInput = ref(null);

const current = computed(() => activityStore.selectedActivity);
const currentId = computed(() => activityStore.idActivity);
const currentTitle = computed(() =>
  current.value?.title ? toTitleCase(current.value.title) : "Série de anotações"
);

async function loadSeries() {
  const textId = textStore.idText;
  if (!textId) return;
  loading.value = true;
  error.value = "";
  try {
    let items = await fetchSeries(textId);

    // Invariante: uma única série "padrão" por (texto, usuário). Se o backend
    // devolveu mais de uma DO PRÓPRIO usuário (dado sujo de versões anteriores),
    // pede o colapso e recarrega uma vez. Defaults de outros usuários no texto
    // compartilhado são legítimos e não contam aqui.
    if (items.filter((s) => isOwnDefault(s)).length > 1) {
      try {
        await activityService.normalizeSeriesDefaults(textId);
        items = await fetchSeries(textId);
      } catch {
        /* mantém a lista como veio */
      }
    }

    series.value = items;
  } catch {
    error.value = "Não foi possível carregar as séries.";
  } finally {
    loading.value = false;
  }
}

async function fetchSeries(textId) {
  const res = await activityService.fetchActivitiesPaged(1, 50, "", textId, false);
  return res.data?.items ?? [];
}

function toggle() {
  open.value = !open.value;
  if (open.value) {
    creating.value = false;
    newTitle.value = "";
    loadSeries();
  }
}

function close() {
  open.value = false;
  creating.value = false;
  newTitle.value = "";
  error.value = "";
}

async function pick(activityId) {
  if (busy.value) return;
  if (activityId === currentId.value) {
    close();
    return;
  }
  busy.value = true;
  error.value = "";
  try {
    await activityStore.switchSeries(activityId);
    close();
  } catch {
    error.value = "Não foi possível trocar de série.";
  } finally {
    busy.value = false;
  }
}

async function startCreating() {
  creating.value = true;
  await nextTick();
  newTitleInput.value?.focus();
}

async function submitNew() {
  if (busy.value) return;
  busy.value = true;
  error.value = "";
  try {
    await activityStore.createSeriesForCurrentText(newTitle.value);
    close();
  } catch (e) {
    error.value =
      e?.response?.data?.message ||
      e?.message ||
      "Não foi possível criar a série.";
  } finally {
    busy.value = false;
  }
}

function manageSeries() {
  const textId = textStore.idText;
  const textTitle = textStore.selectedText?.title;
  close();
  router.push({ name: "ActivityForm", params: { textId, textTitle } });
}

function openCard() {
  const id = currentId.value;
  close();
  emit("open-activity-card", id);
}

function onDocPointer(e) {
  if (!e.target.closest?.(".series-switcher")) open.value = false;
}
onMounted(() => document.addEventListener("pointerdown", onDocPointer));
onBeforeUnmount(() =>
  document.removeEventListener("pointerdown", onDocPointer)
);
</script>

<template>
  <div class="series-switcher">
    <button
      type="button"
      class="series-trigger"
      :class="{ 'is-open': open }"
      :title="currentTitle"
      :aria-expanded="open"
      aria-haspopup="menu"
      @click="toggle"
    >
      <span class="series-trigger-text">{{ currentTitle }}</span>
      <i class="fas fa-chevron-down series-chevron" aria-hidden="true"></i>
    </button>

    <div v-if="open" class="series-menu" role="menu">
      <div class="series-menu-head">Séries de anotações deste texto</div>

      <div v-if="loading" class="series-menu-state">Carregando…</div>

      <ul v-else class="series-list">
        <li v-for="s in series" :key="s.activityId">
          <button
            type="button"
            class="series-item"
            :class="{ 'is-current': s.activityId === currentId }"
            role="menuitemradio"
            :aria-checked="s.activityId === currentId"
            :disabled="busy"
            @click="pick(s.activityId)"
          >
            <span class="series-item-main">
              <span class="series-item-title">{{ toTitleCase(s.title) }}</span>
              <span class="series-item-meta">
                <span class="series-count">{{ s.noteCount || 0 }} nota(s)</span>
                <span class="series-owner">{{
                  isOwn(s)
                    ? "Criada por mim"
                    : `Criador: ${s.createdByUserName || "—"}`
                }}</span>
              </span>
            </span>
            <i
              v-if="s.activityId === currentId"
              class="fas fa-check series-check"
              aria-hidden="true"
            ></i>
          </button>
        </li>
      </ul>

      <div v-if="error" class="series-menu-error">{{ error }}</div>

      <div class="series-menu-foot">
        <template v-if="creating">
          <form class="series-new-form" @submit.prevent="submitNew">
            <input
              ref="newTitleInput"
              v-model="newTitle"
              type="text"
              class="series-new-input"
              placeholder="Nome da nova série"
              maxlength="255"
              :disabled="busy"
            />
            <div class="series-new-actions">
              <button
                type="button"
                class="series-btn ghost"
                :disabled="busy"
                @click="creating = false"
              >
                Cancelar
              </button>
              <button
                type="submit"
                class="series-btn primary"
                :disabled="busy || newTitle.trim().length < 3"
              >
                Criar
              </button>
            </div>
          </form>
        </template>

        <template v-else>
          <div class="series-new-row">
            <button
              type="button"
              class="series-new-btn"
              :disabled="busy"
              @click="startCreating"
            >
              <i class="fas fa-plus" aria-hidden="true"></i> Nova série de
              anotações
            </button>
          </div>
          <button type="button" class="series-action" @click="openCard">
            <i class="fas fa-circle-info" aria-hidden="true"></i> Ver detalhes da
            série
          </button>
          <button type="button" class="series-action" @click="manageSeries">
            <i class="fas fa-sliders" aria-hidden="true"></i> Gerenciar séries
            deste texto
          </button>
        </template>
      </div>
    </div>
  </div>
</template>

<style scoped>
.series-switcher {
  position: relative;
  margin: 0 0 16px;
  max-width: 100%;
}

.series-trigger {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  max-width: 100%;
  padding: 4px 8px;
  /* Sem margin-left negativa: .reader-scroll tem overflow-x: hidden e recortaria
     a borda e os cantos arredondados da esquerda no hover. */
  margin-left: 0;
  border: 1px solid transparent;
  border-radius: 10px;
  background: transparent;
  font-size: 1.05rem;
  font-weight: 600;
  line-height: 1.15;
  color: inherit;
  cursor: pointer;
  text-align: left;
  transition: background-color 0.15s ease, border-color 0.15s ease,
    transform 0.05s ease;
}

.series-trigger:hover,
.series-trigger.is-open {
  border-color: rgba(202, 138, 4, 0.35);
  background: rgba(202, 138, 4, 0.08);
}
/* Ao pressionar, afunda — mesmo efeito dos botões do cabeçalho da atividade. */
.series-trigger:active,
.series-action:active:not(:disabled),
.series-new-btn:active:not(:disabled) {
  transform: translateY(1px);
}

.series-trigger-text {
  min-width: 0;
  word-break: break-word;
  overflow-wrap: break-word;
}

.series-chevron {
  font-size: 0.7rem;
  flex-shrink: 0;
  opacity: 0.7;
  transition: transform 0.15s ease;
}
.series-trigger.is-open .series-chevron {
  transform: rotate(180deg);
}

.series-menu {
  position: absolute;
  top: calc(100% + 4px);
  /* .reader-scroll (ancestral) tem overflow-x: hidden — um left negativo faz o
     menu ser recortado e "come" os cantos arredondados da esquerda. */
  left: 0;
  z-index: 40;
  width: 320px;
  max-width: 88vw;
  background: var(--bg-content);
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 12px;
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.16);
  overflow: hidden;
}

.series-menu-head {
  padding: 10px 14px;
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  opacity: 0.65;
}

.series-menu-state,
.series-menu-error {
  padding: 10px 14px;
  font-size: 0.85rem;
}
.series-menu-error {
  color: var(--danger-text);
}

.series-list {
  list-style: none;
  margin: 0;
  padding: 0;
  /* Mostra ~4-5 séries; o resto rola. O rodapé (nova série / ações) fica fora
     desta caixa, depois da barra de rolagem. */
  max-height: calc(3.6rem * 4.6);
  overflow-y: auto;
}

.series-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  width: 100%;
  min-height: 3.6rem;
  padding: 9px 14px;
  border: none;
  background: transparent;
  cursor: pointer;
  text-align: left;
  font: inherit;
}
.series-item:hover:not(:disabled) {
  background: rgba(0, 0, 0, 0.05);
}
.series-item:disabled {
  cursor: default;
  opacity: 0.6;
}
.series-item.is-current {
  background: rgba(155, 132, 255, 0.16);
}

.series-item-main {
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
}
.series-item-title {
  font-size: 0.9rem;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.series-item-meta {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.72rem;
  opacity: 0.8;
  flex-wrap: wrap;
}
.series-owner {
  font-style: italic;
  opacity: 0.75;
}
.series-check {
  color: var(--brand-hover);
  flex-shrink: 0;
}

.series-menu-foot {
  border-top: 1px solid rgba(0, 0, 0, 0.1);
  padding: 6px;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.series-action {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 8px 10px;
  border: none;
  border-radius: 8px;
  background: transparent;
  cursor: pointer;
  font-size: 0.85rem;
  text-align: left;
  transition: background-color 0.15s ease, transform 0.05s ease;
}
.series-action:hover:not(:disabled) {
  background: rgba(0, 0, 0, 0.06);
}
.series-action:disabled {
  opacity: 0.5;
  cursor: default;
}

/* "Nova série" como o botão da chip "Total de notas" do TextDisplayer.vue
   (.stat-chip--total + .stat-chip--button): mesma pílula, cor e hover. */
.series-new-row {
  padding: 4px 4px 8px;
}
.series-new-btn {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  height: 26px;
  padding: 0 12px;
  border-radius: 999px;
  font-size: 0.78rem;
  font-weight: 700;
  line-height: 1;
  white-space: nowrap;
  font-family: inherit;
  cursor: pointer;
  background-color: #ece7ff;
  color: #4b3f86;
  border: 1px solid #ded5ff;
  transition: background-color 0.15s ease, transform 0.05s ease;
}
.series-new-btn i {
  font-size: 0.72rem;
  opacity: 0.85;
}
.series-new-btn:hover:not(:disabled) {
  background-color: #ded5ff;
}
.series-new-btn:disabled {
  opacity: 0.5;
  cursor: default;
}

.series-new-form {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 6px;
}
.series-new-input {
  width: 100%;
  padding: 8px 10px;
  border: 1px solid rgba(0, 0, 0, 0.2);
  border-radius: 8px;
  font: inherit;
  font-size: 0.85rem;
}
.series-new-actions {
  display: flex;
  justify-content: flex-end;
  gap: 6px;
}
.series-btn {
  padding: 6px 12px;
  border-radius: 10px;
  border: 1px solid transparent;
  cursor: pointer;
  font-size: 0.82rem;
  font-weight: 500;
  transition: background-color 0.2s ease-in-out, transform 0.05s ease;
}
.series-btn.ghost {
  background: transparent;
  border-color: rgba(0, 0, 0, 0.2);
}
/* Mesmo roxo do "Adicionar Atividade" (btn-success) do AddActivityForm.vue */
.series-btn.primary {
  background-color: var(--brand);
  color: var(--text-on-brand);
}
.series-btn.primary:hover:not(:disabled) {
  background-color: var(--brand-hover);
}
.series-btn.primary:active:not(:disabled) {
  transform: translateY(1px);
}
.series-btn:disabled {
  opacity: 0.5;
  cursor: default;
}

@media (prefers-color-scheme: dark) {
  .series-menu {
    background: var(--bg-content);
    border-color: rgba(255, 255, 255, 0.14);
  }
  .series-item:hover:not(:disabled),
  .series-action:hover:not(:disabled) {
    background: rgba(255, 255, 255, 0.08);
  }
  .series-menu-foot,
  .series-menu {
    border-color: rgba(255, 255, 255, 0.12);
  }
  /* .series-new-btn NÃO tem variante escura de propósito: espelha a chip
     "Total de notas" do TextDisplayer, que mantém a mesma cor em qualquer tema. */
}
</style>
